const API_URL =
  import.meta.env.VITE_API_URL?.trim() ||
  import.meta.env.VITE_API_BASE?.trim() ||
  '/api';

interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
}

interface User {
  id: string;
  name: string;
  employeeNumber: string;
  role:
    | 'SYSTEM_ADMIN'
    | 'ADMIN'
    | 'GENERAL_MANAGER'
    | 'ZONE_MANAGER'
    | 'REPRESENTATIVE'
    | 'SELLER';
  avatarUrl?: string;
}

interface LoginResponse {
  accessToken: string;
  user: User;
}

interface DashboardData {
  user: User;
  hasReproveAccess: boolean;
  welcomeMessage: string;
}

// Mock data para desarrollo
const MOCK_USERS: Array<{
  employeeNumber: string;
  password: string;
  user: User;
}> = [
  {
    employeeNumber: '0001',
    password: 'admin123',
    user: {
      id: 'user_system_admin',
      name: 'Administrador del Sistema',
      employeeNumber: '0001',
      role: 'SYSTEM_ADMIN',
    },
  },
  {
    employeeNumber: '0002',
    password: 'admin123',
    user: {
      id: 'user_admin',
      name: 'Administrador',
      employeeNumber: '0002',
      role: 'ADMIN',
    },
  },
  {
    employeeNumber: '1000',
    password: 'gm123',
    user: {
      id: 'user_gm',
      name: 'María José Hernández',
      employeeNumber: '1000',
      role: 'GENERAL_MANAGER',
    },
  },
  {
    employeeNumber: '2000',
    password: 'zm123',
    user: {
      id: 'user_zm',
      name: 'Carlos Rodríguez',
      employeeNumber: '2000',
      role: 'ZONE_MANAGER',
    },
  },
  {
    employeeNumber: '3000',
    password: 'rep123',
    user: {
      id: 'user_rep',
      name: 'Eric Nicacio Castillo',
      employeeNumber: '3000',
      role: 'REPRESENTATIVE',
    },
  },
  {
    employeeNumber: '4000',
    password: 'seller123',
    user: {
      id: 'user_seller',
      name: 'Ana García',
      employeeNumber: '4000',
      role: 'SELLER',
    },
  },
];

class ApiClient {
  private baseURL: string;
  private isApiAvailable: boolean | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('accessToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  private handleUnauthorized(): void {
    // Limpiar tokens inválidos
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');

    // Recargar página para forzar re-login
    window.location.reload();
  }

  // Verificar si la API está disponible
  private async checkApiAvailability(): Promise<boolean> {
    if (this.isApiAvailable !== null) {
      return this.isApiAvailable;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 segundos timeout

      const response = await fetch(`${this.baseURL}/health`, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      this.isApiAvailable = response.ok;

      return this.isApiAvailable;
    } catch (error) {
      this.isApiAvailable = false;
      return false;
    }
  }

  // Métodos mock para desarrollo
  private async mockLogin(
    employeeNumber: string,
    password: string
  ): Promise<LoginResponse> {
    await new Promise((resolve) => setTimeout(resolve, 800));

    // Buscar usuario en mock data
    const mockUser = MOCK_USERS.find(
      (u) => u.employeeNumber === employeeNumber && u.password === password
    );

    if (!mockUser) {
      throw new Error('Credenciales incorrectas');
    }

    // Generar token mock
    const accessToken = `mock_token_${mockUser.user.id}_${Date.now()}`;

    return {
      accessToken,
      user: mockUser.user,
    };
  }

  private async mockProfileValidation(): Promise<User> {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      throw new Error('No hay datos de usuario');
    }

    const user = JSON.parse(userStr);
    if (!user.id || !user.name || !user.role) {
      throw new Error('Datos de usuario inválidos');
    }

    return user;
  }

  private async mockDashboard(): Promise<DashboardData> {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      throw new Error('No hay datos de usuario');
    }

    const user = JSON.parse(userStr);

    return {
      user,
      hasReproveAccess: true,
      welcomeMessage: `Bienvenido, ${user.name}`,
    };
  }

  // Método principal para hacer requests
  private async handleResponse<T>(response: Response): Promise<T> {
    // Si es 401, limpiar tokens y recargar
    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Sesión expirada');
    }

    // Si no es ok, lanzar error
    if (!response.ok) {
      let errorMessage = `Error ${response.status}: ${response.statusText}`;

      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch {
        // Si no se puede parsear el error, usar el mensaje por defecto
      }

      throw new Error(errorMessage);
    }

    // Intentar parsear JSON
    try {
      return await response.json();
    } catch {
      // Si no es JSON, devolver response como texto
      return (await response.text()) as unknown as T;
    }
  }

  async get<T = any>(endpoint: string): Promise<T> {
    const isApiAvailable = await this.checkApiAvailability();

    // Rutas específicas con fallback a mock
    if (endpoint === '/auth/profile') {
      if (isApiAvailable) {
        try {
          const response = await fetch(`${this.baseURL}${endpoint}`, {
            method: 'GET',
            headers: this.getAuthHeaders(),
          });
          return this.handleResponse<T>(response);
        } catch (error) {
          return this.mockProfileValidation() as unknown as T;
        }
      } else {
        return this.mockProfileValidation() as unknown as T;
      }
    }

    if (endpoint === '/dashboard') {
      if (isApiAvailable) {
        try {
          const response = await fetch(`${this.baseURL}${endpoint}`, {
            method: 'GET',
            headers: this.getAuthHeaders(),
          });
          return this.handleResponse<T>(response);
        } catch (error) {
          return this.mockDashboard() as unknown as T;
        }
      } else {
        return this.mockDashboard() as unknown as T;
      }
    }

    // Para otras rutas, intentar API primero
    if (isApiAvailable) {
      try {
        const response = await fetch(`${this.baseURL}${endpoint}`, {
          method: 'GET',
          headers: this.getAuthHeaders(),
        });
        return this.handleResponse<T>(response);
      } catch (error) {
        throw error;
      }
    } else {
      throw new Error('API no disponible y no hay mock para esta ruta');
    }
  }

  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    const isApiAvailable = await this.checkApiAvailability();

    // Login con fallback a mock
    if (endpoint === '/auth/login') {
      if (isApiAvailable) {
        try {
          const response = await fetch(`${this.baseURL}${endpoint}`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: data ? JSON.stringify(data) : undefined,
          });
          return this.handleResponse<T>(response);
        } catch (error) {
          return this.mockLogin(
            data.employeeNumber,
            data.password
          ) as unknown as T;
        }
      } else {
        return this.mockLogin(
          data.employeeNumber,
          data.password
        ) as unknown as T;
      }
    }

    // Para otras rutas POST, intentar API
    if (isApiAvailable) {
      try {
        const response = await fetch(`${this.baseURL}${endpoint}`, {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: data ? JSON.stringify(data) : undefined,
        });
        return this.handleResponse<T>(response);
      } catch (error) {
        throw error;
      }
    } else {
      throw new Error('API no disponible y no hay mock para esta ruta');
    }
  }

  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    const isApiAvailable = await this.checkApiAvailability();

    if (isApiAvailable) {
      try {
        const response = await fetch(`${this.baseURL}${endpoint}`, {
          method: 'PUT',
          headers: this.getAuthHeaders(),
          body: data ? JSON.stringify(data) : undefined,
        });
        return await this.handleResponse<T>(response);
      } catch (err: unknown) {
        const error = err instanceof Error ? err : new Error(String(err));
        throw error;
      }
    } else {
      throw new Error('API no disponible');
    }
  }
  async patch<T = any>(endpoint: string, data?: any): Promise<T> {
    const isApiAvailable = await this.checkApiAvailability();

    if (isApiAvailable) {
      try {
        const response = await fetch(`${this.baseURL}${endpoint}`, {
          method: 'PATCH',
          headers: this.getAuthHeaders(),
          body: data ? JSON.stringify(data) : undefined,
        });
        return await this.handleResponse<T>(response);
      } catch (err: unknown) {
        const error = err instanceof Error ? err : new Error(String(err));
        throw error;
      }
    } else {
      throw new Error('API no disponible');
    }
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    const isApiAvailable = await this.checkApiAvailability();

    if (isApiAvailable) {
      try {
        const response = await fetch(`${this.baseURL}${endpoint}`, {
          method: 'DELETE',
          headers: this.getAuthHeaders(),
        });
        return this.handleResponse<T>(response);
      } catch (error) {
        throw error;
      }
    } else {
      throw new Error('API no disponible');
    }
  }

  // Método para resetear el estado de disponibilidad de la API
  resetApiAvailability(): void {
    this.isApiAvailable = null;
  }
}

// Instancia singleton
export const apiClient = new ApiClient(API_URL);

// Hook personalizado para usar el cliente API
export const useApi = () => {
  return {
    get: apiClient.get.bind(apiClient),
    post: apiClient.post.bind(apiClient),
    put: apiClient.put.bind(apiClient),
    patch: apiClient.patch.bind(apiClient),
    delete: apiClient.delete.bind(apiClient),
    resetApiAvailability: apiClient.resetApiAvailability.bind(apiClient),
  };
};
